import { Component } from '@angular/core';
import { NgIf, NgFor } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgbActiveModal, NgbTypeahead } from '@ng-bootstrap/ng-bootstrap';
import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { Observable, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, switchMap, catchError } from 'rxjs/operators';
import { StoreSearchEntry } from 'src/app/models/store-search-entry';
import { FolderPlanogram } from 'src/app/models/folder';
import { ApiService } from 'src/app/services/api.service';
import { PlanogramApiService } from 'src/app/services/planogram-api.service';

/** One planogram chosen for the combined PDF, plus the saved state to load it from. */
interface SelectedPlanogram {
  planogram: FolderPlanogram;
  stateId: string;
}

/**
 * Combined Planograms PDF tool. Pick a store, choose planograms under it, drag to set the stitch
 * order and choose a state per planogram, then export a single PDF that stitches their drops together
 * (Overview + Drop Analysis). Nothing is saved — the PDF is generated in memory server-side.
 */
@Component({
  selector: 'app-combined-planograms-pdf-modal',
  templateUrl: './combined-planograms-pdf-modal.component.html',
  imports: [FormsModule, NgbTypeahead, NgIf, NgFor, DragDropModule],
})
export class CombinedPlanogramsPdfModalComponent {
  storeModel: string | StoreSearchEntry = '';
  selectedStore: StoreSearchEntry | null = null;
  availablePlanograms: FolderPlanogram[] = [];
  selected: SelectedPlanogram[] = [];
  isLoadingPlanograms = false;
  isExporting = false;

  constructor(
    public activeModal: NgbActiveModal,
    private apiService: ApiService,
    private planogramApiService: PlanogramApiService,
  ) {}

  searchStores = (text$: Observable<string>) =>
    text$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      filter((term) => term.length >= 2),
      switchMap((term) => this.apiService.searchStores(term).pipe(catchError(() => of([])))),
    );

  storeFormatter = (store: StoreSearchEntry) => store.name;

  async onStoreSelect(event: { item: StoreSearchEntry; preventDefault: () => void }): Promise<void> {
    event.preventDefault();
    const store = event.item;
    this.storeModel = '';
    this.selectedStore = store;
    this.availablePlanograms = [];
    this.selected = [];
    if (!store) return;

    this.isLoadingPlanograms = true;
    try {
      this.availablePlanograms = await this.planogramApiService.getPlanogramsByStore(store.storeGuid);
    } catch (err) {
      // apiRequest already surfaced a danger toast.
      console.error(err);
    } finally {
      this.isLoadingPlanograms = false;
    }
  }

  isSelected(planogram: FolderPlanogram): boolean {
    return this.selected.some((s) => s.planogram.guid === planogram.guid);
  }

  togglePlanogram(planogram: FolderPlanogram): void {
    if (this.isSelected(planogram)) {
      this.selected = this.selected.filter((s) => s.planogram.guid !== planogram.guid);
      return;
    }
    const defaultStateId = this.defaultStateId(planogram);
    if (!defaultStateId) return; // nothing to load (should not happen — listing only returns stateful planograms)
    this.selected = [...this.selected, { planogram, stateId: defaultStateId }];
  }

  removeSelected(item: SelectedPlanogram): void {
    this.selected = this.selected.filter((s) => s !== item);
  }

  drop(event: CdkDragDrop<SelectedPlanogram[]>): void {
    moveItemInArray(this.selected, event.previousIndex, event.currentIndex);
  }

  /** The most recently saved state — the default selection for a planogram. */
  private defaultStateId(planogram: FolderPlanogram): string | null {
    const states = planogram.states ?? [];
    if (states.length === 0) return null;
    const sorted = [...states].sort(
      (a, b) => new Date(b.dateCreated).getTime() - new Date(a.dateCreated).getTime(),
    );
    return sorted[0].guid;
  }

  latestStateName(planogram: FolderPlanogram): string {
    const id = this.defaultStateId(planogram);
    return (planogram.states ?? []).find((s) => s.guid === id)?.name ?? '';
  }

  async export(): Promise<void> {
    if (this.selected.length === 0 || this.isExporting || !this.selectedStore) return;
    this.isExporting = true;
    try {
      await this.planogramApiService.exportCombinedPlanogram({
        storeGuid: this.selectedStore.storeGuid,
        items: this.selected.map((s) => ({ planogramId: s.planogram.guid, stateId: s.stateId })),
      });
      this.activeModal.close(true);
    } catch (err) {
      // exportCombinedPlanogram already surfaced a danger toast.
      console.error(err);
    } finally {
      this.isExporting = false;
    }
  }

  cancel(): void {
    this.activeModal.close(null);
  }
}
